/**
 * Team Templates Library
 *
 * Pre-configured team templates for common consulting scenarios.
 * These align with the 9 specialized teams from the PRD.
 */

import { TeamCoordinationStrategy, AgentRole, type AgentTeamCreate } from '@/types'

export interface TeamTemplate {
  id: string
  name: string
  description: string
  category: 'consulting' | 'technical' | 'creative' | 'analysis' | 'general'
  icon: string
  teamConfig: Omit<AgentTeamCreate, 'workspace_id' | 'created_by'>
  recommendedAgents: {
    role: AgentRole
    name: string
    description: string
    systemPrompt: string
    executionOrder: number
    isRequired: boolean
  }[]
}

export const TEAM_TEMPLATES: TeamTemplate[] = [
  // 1. Research & Intelligence Team
  {
    id: 'research-intelligence',
    name: 'Research & Intelligence Team',
    description: 'Comprehensive research, data collection, and competitive analysis. Perfect for market research, industry analysis, and strategic insights.',
    category: 'consulting',
    icon: '🔍',
    teamConfig: {
      name: 'Research & Intelligence Team',
      description: 'Multi-agent team for comprehensive research and competitive intelligence gathering',
      strategy: TeamCoordinationStrategy.SEQUENTIAL,
      configuration: {
        qualityThreshold: 0.85,
        parallelism: 2,
      },
      feedback_enabled: true,
      max_iterations: 3,
      members: [],
    },
    recommendedAgents: [
      {
        role: AgentRole.COORDINATOR,
        name: 'Research Coordinator',
        description: 'Orchestrates research tasks and synthesizes findings',
        systemPrompt: 'You are a Research Coordinator. Break down research objectives into specific tasks, coordinate with specialist researchers, and synthesize findings into coherent insights. Ensure thoroughness and accuracy.',
        executionOrder: 1,
        isRequired: true,
      },
      {
        role: AgentRole.RESEARCHER,
        name: 'Primary Researcher',
        description: 'Conducts primary research and data collection',
        systemPrompt: 'You are a Primary Researcher. Gather information from multiple sources, conduct in-depth analysis, and provide comprehensive research reports. Focus on factual accuracy and source credibility.',
        executionOrder: 2,
        isRequired: true,
      },
      {
        role: AgentRole.ANALYST,
        name: 'Data Analyst',
        description: 'Analyzes research data and identifies patterns',
        systemPrompt: 'You are a Data Analyst. Analyze research data, identify trends and patterns, and provide quantitative insights. Use statistical methods where appropriate.',
        executionOrder: 3,
        isRequired: true,
      },
      {
        role: AgentRole.RESEARCHER,
        name: 'Competitive Intelligence Specialist',
        description: 'Focuses on competitive analysis',
        systemPrompt: 'You are a Competitive Intelligence Specialist. Analyze competitors, market positioning, and strategic movements. Provide actionable competitive insights.',
        executionOrder: 4,
        isRequired: false,
      },
      {
        role: AgentRole.REVIEWER,
        name: 'Quality Reviewer',
        description: 'Reviews research for accuracy and completeness',
        systemPrompt: 'You are a Quality Reviewer. Review research findings for accuracy, completeness, and credibility. Identify gaps and recommend additional research if needed.',
        executionOrder: 5,
        isRequired: true,
      },
    ],
  },

  // 2. Strategic Writing Team
  {
    id: 'strategic-writing',
    name: 'Strategic Writing Team',
    description: 'Professional document creation including proposals, reports, white papers, and presentations. Combines research with compelling narrative.',
    category: 'creative',
    icon: '✍️',
    teamConfig: {
      name: 'Strategic Writing Team',
      description: 'Multi-agent team for creating high-quality strategic documents and proposals',
      strategy: TeamCoordinationStrategy.SEQUENTIAL,
      configuration: {
        qualityThreshold: 0.9,
      },
      feedback_enabled: true,
      max_iterations: 4,
      members: [],
    },
    recommendedAgents: [
      {
        role: AgentRole.COORDINATOR,
        name: 'Content Strategist',
        description: 'Plans document structure and messaging',
        systemPrompt: 'You are a Content Strategist. Define document structure, key messages, and narrative flow. Ensure alignment with business objectives and audience needs.',
        executionOrder: 1,
        isRequired: true,
      },
      {
        role: AgentRole.RESEARCHER,
        name: 'Content Researcher',
        description: 'Gathers supporting data and evidence',
        systemPrompt: 'You are a Content Researcher. Gather relevant data, statistics, case studies, and supporting evidence for the document. Ensure all claims are well-supported.',
        executionOrder: 2,
        isRequired: true,
      },
      {
        role: AgentRole.WRITER,
        name: 'Lead Writer',
        description: 'Creates the primary content',
        systemPrompt: 'You are a Lead Writer. Craft compelling, professional content that communicates complex ideas clearly. Maintain consistent tone and style throughout.',
        executionOrder: 3,
        isRequired: true,
      },
      {
        role: AgentRole.WRITER,
        name: 'Technical Writer',
        description: 'Handles technical sections',
        systemPrompt: 'You are a Technical Writer. Create clear technical documentation, explain complex concepts, and ensure technical accuracy while maintaining readability.',
        executionOrder: 4,
        isRequired: false,
      },
      {
        role: AgentRole.REVIEWER,
        name: 'Editor',
        description: 'Reviews and refines content',
        systemPrompt: 'You are an Editor. Review content for clarity, coherence, grammar, and style. Ensure professional quality and consistency. Provide constructive feedback for improvements.',
        executionOrder: 5,
        isRequired: true,
      },
    ],
  },

  // 3. Design & Visualization Team
  {
    id: 'design-visualization',
    name: 'Design & Visualization Team',
    description: 'Creates visual content including diagrams, infographics, and presentation decks. Transforms data into compelling visuals.',
    category: 'creative',
    icon: '🎨',
    teamConfig: {
      name: 'Design & Visualization Team',
      description: 'Multi-agent team for creating visual content and data visualizations',
      strategy: TeamCoordinationStrategy.PARALLEL,
      configuration: {
        maxParallel: 3,
      },
      feedback_enabled: true,
      max_iterations: 3,
      members: [],
    },
    recommendedAgents: [
      {
        role: AgentRole.COORDINATOR,
        name: 'Creative Director',
        description: 'Oversees visual strategy and design direction',
        systemPrompt: 'You are a Creative Director. Define visual strategy, design principles, and ensure brand consistency. Guide the design team to create impactful visuals.',
        executionOrder: 1,
        isRequired: true,
      },
      {
        role: AgentRole.SPECIALIST,
        name: 'Data Visualization Specialist',
        description: 'Creates charts and infographics',
        systemPrompt: 'You are a Data Visualization Specialist. Transform data into clear, compelling visualizations. Choose appropriate chart types and design for maximum insight communication.',
        executionOrder: 2,
        isRequired: true,
      },
      {
        role: AgentRole.SPECIALIST,
        name: 'Presentation Designer',
        description: 'Designs presentation decks',
        systemPrompt: 'You are a Presentation Designer. Create professional, visually appealing presentation decks. Balance aesthetics with information clarity.',
        executionOrder: 3,
        isRequired: false,
      },
      {
        role: AgentRole.REVIEWER,
        name: 'Design Reviewer',
        description: 'Reviews visual quality and consistency',
        systemPrompt: 'You are a Design Reviewer. Evaluate visual designs for quality, consistency, and effectiveness. Ensure alignment with brand guidelines and accessibility standards.',
        executionOrder: 4,
        isRequired: true,
      },
    ],
  },

  // 4. Full-Stack Development Team
  {
    id: 'full-stack-dev',
    name: 'Full-Stack Development Team',
    description: 'End-to-end software development including architecture, frontend, backend, and testing. Builds production-ready applications.',
    category: 'technical',
    icon: '💻',
    teamConfig: {
      name: 'Full-Stack Development Team',
      description: 'Multi-agent team for complete software development lifecycle',
      strategy: TeamCoordinationStrategy.SEQUENTIAL,
      configuration: {
        qualityThreshold: 0.95,
        testCoverage: 80,
      },
      feedback_enabled: true,
      max_iterations: 5,
      members: [],
    },
    recommendedAgents: [
      {
        role: AgentRole.COORDINATOR,
        name: 'Tech Lead',
        description: 'Oversees technical architecture and development',
        systemPrompt: 'You are a Tech Lead. Define technical architecture, coordinate development tasks, and ensure code quality. Make technology decisions and mentor the team.',
        executionOrder: 1,
        isRequired: true,
      },
      {
        role: AgentRole.SPECIALIST,
        name: 'Frontend Developer',
        description: 'Builds user interfaces',
        systemPrompt: 'You are a Frontend Developer. Build responsive, accessible user interfaces using modern frameworks. Focus on user experience and performance.',
        executionOrder: 2,
        isRequired: true,
      },
      {
        role: AgentRole.SPECIALIST,
        name: 'Backend Developer',
        description: 'Develops server-side logic and APIs',
        systemPrompt: 'You are a Backend Developer. Design and implement robust APIs, business logic, and data models. Ensure security, scalability, and performance.',
        executionOrder: 3,
        isRequired: true,
      },
      {
        role: AgentRole.TESTER,
        name: 'QA Engineer',
        description: 'Tests and ensures quality',
        systemPrompt: 'You are a QA Engineer. Write and execute comprehensive tests, identify bugs, and ensure software quality. Focus on both automated and manual testing.',
        executionOrder: 4,
        isRequired: true,
      },
      {
        role: AgentRole.DEBUGGER,
        name: 'DevOps Engineer',
        description: 'Handles deployment and infrastructure',
        systemPrompt: 'You are a DevOps Engineer. Set up CI/CD pipelines, manage infrastructure, and ensure smooth deployments. Monitor performance and reliability.',
        executionOrder: 5,
        isRequired: false,
      },
    ],
  },

  // 5. Data Science & Analytics Team
  {
    id: 'data-science',
    name: 'Data Science & Analytics Team',
    description: 'Advanced data analysis, ML modeling, and statistical insights. Turns data into actionable intelligence.',
    category: 'analysis',
    icon: '📊',
    teamConfig: {
      name: 'Data Science & Analytics Team',
      description: 'Multi-agent team for data analysis and machine learning',
      strategy: TeamCoordinationStrategy.SEQUENTIAL,
      configuration: {
        modelAccuracyThreshold: 0.85,
      },
      feedback_enabled: true,
      max_iterations: 4,
      members: [],
    },
    recommendedAgents: [
      {
        role: AgentRole.COORDINATOR,
        name: 'Data Science Lead',
        description: 'Oversees data science projects',
        systemPrompt: 'You are a Data Science Lead. Define analytical approach, coordinate data science tasks, and ensure methodological rigor. Translate business questions into analytical problems.',
        executionOrder: 1,
        isRequired: true,
      },
      {
        role: AgentRole.ANALYST,
        name: 'Data Engineer',
        description: 'Prepares and processes data',
        systemPrompt: 'You are a Data Engineer. Build data pipelines, clean and transform data, and ensure data quality. Prepare datasets for analysis and modeling.',
        executionOrder: 2,
        isRequired: true,
      },
      {
        role: AgentRole.SPECIALIST,
        name: 'ML Engineer',
        description: 'Builds machine learning models',
        systemPrompt: 'You are a Machine Learning Engineer. Build, train, and optimize ML models. Select appropriate algorithms and evaluate model performance.',
        executionOrder: 3,
        isRequired: true,
      },
      {
        role: AgentRole.ANALYST,
        name: 'Statistical Analyst',
        description: 'Performs statistical analysis',
        systemPrompt: 'You are a Statistical Analyst. Conduct statistical tests, interpret results, and provide quantitative insights. Ensure statistical validity and significance.',
        executionOrder: 4,
        isRequired: true,
      },
      {
        role: AgentRole.VALIDATOR,
        name: 'Model Validator',
        description: 'Validates models and results',
        systemPrompt: 'You are a Model Validator. Validate model assumptions, check for bias, and ensure reproducibility. Review analytical methodology for soundness.',
        executionOrder: 5,
        isRequired: true,
      },
    ],
  },

  // 6. Project Management Team
  {
    id: 'project-management',
    name: 'Project Management Team',
    description: 'Plans, coordinates, and tracks complex projects. Ensures on-time, on-budget delivery.',
    category: 'consulting',
    icon: '📋',
    teamConfig: {
      name: 'Project Management Team',
      description: 'Multi-agent team for comprehensive project planning and execution',
      strategy: TeamCoordinationStrategy.HIERARCHICAL,
      configuration: {
        updateFrequency: 'daily',
      },
      feedback_enabled: true,
      max_iterations: 3,
      members: [],
    },
    recommendedAgents: [
      {
        role: AgentRole.COORDINATOR,
        name: 'Project Manager',
        description: 'Oversees entire project lifecycle',
        systemPrompt: 'You are a Project Manager. Plan projects, manage resources, track progress, and ensure successful delivery. Communicate with stakeholders and mitigate risks.',
        executionOrder: 1,
        isRequired: true,
      },
      {
        role: AgentRole.SPECIALIST,
        name: 'Business Analyst',
        description: 'Gathers and analyzes requirements',
        systemPrompt: 'You are a Business Analyst. Gather requirements, document processes, and ensure alignment between business needs and project deliverables.',
        executionOrder: 2,
        isRequired: true,
      },
      {
        role: AgentRole.SPECIALIST,
        name: 'Resource Manager',
        description: 'Manages team allocation and capacity',
        systemPrompt: 'You are a Resource Manager. Optimize resource allocation, track capacity, and ensure efficient team utilization. Balance workload across team members.',
        executionOrder: 3,
        isRequired: false,
      },
      {
        role: AgentRole.ANALYST,
        name: 'Risk Analyst',
        description: 'Identifies and manages project risks',
        systemPrompt: 'You are a Risk Analyst. Identify potential risks, assess impact and probability, and develop mitigation strategies. Monitor risk indicators throughout the project.',
        executionOrder: 4,
        isRequired: true,
      },
    ],
  },

  // 7. Quality Assurance & Testing Team
  {
    id: 'qa-testing',
    name: 'Quality Assurance & Testing Team',
    description: 'Comprehensive testing including functional, performance, and security testing. Ensures product quality.',
    category: 'technical',
    icon: '✅',
    teamConfig: {
      name: 'Quality Assurance & Testing Team',
      description: 'Multi-agent team for comprehensive quality assurance',
      strategy: TeamCoordinationStrategy.PARALLEL,
      configuration: {
        testCoverage: 90,
        maxParallel: 4,
      },
      feedback_enabled: true,
      max_iterations: 3,
      members: [],
    },
    recommendedAgents: [
      {
        role: AgentRole.COORDINATOR,
        name: 'QA Lead',
        description: 'Oversees testing strategy',
        systemPrompt: 'You are a QA Lead. Define testing strategy, coordinate test execution, and ensure comprehensive coverage. Manage defect triage and quality metrics.',
        executionOrder: 1,
        isRequired: true,
      },
      {
        role: AgentRole.TESTER,
        name: 'Functional Tester',
        description: 'Tests functional requirements',
        systemPrompt: 'You are a Functional Tester. Design and execute functional tests, verify requirements, and report defects. Ensure the system works as specified.',
        executionOrder: 2,
        isRequired: true,
      },
      {
        role: AgentRole.TESTER,
        name: 'Automation Engineer',
        description: 'Builds automated test suites',
        systemPrompt: 'You are an Automation Engineer. Design and implement automated test frameworks. Maximize test coverage through automation while ensuring maintainability.',
        executionOrder: 3,
        isRequired: true,
      },
      {
        role: AgentRole.SPECIALIST,
        name: 'Performance Tester',
        description: 'Tests performance and scalability',
        systemPrompt: 'You are a Performance Tester. Design and execute performance tests, identify bottlenecks, and recommend optimizations. Ensure system meets performance requirements.',
        executionOrder: 4,
        isRequired: false,
      },
      {
        role: AgentRole.SPECIALIST,
        name: 'Security Tester',
        description: 'Tests for security vulnerabilities',
        systemPrompt: 'You are a Security Tester. Identify security vulnerabilities, conduct penetration testing, and ensure secure coding practices. Follow OWASP guidelines.',
        executionOrder: 5,
        isRequired: false,
      },
    ],
  },

  // 8. Business Strategy & Planning Team
  {
    id: 'business-strategy',
    name: 'Business Strategy & Planning Team',
    description: 'Strategic planning, market analysis, and business model development. Guides long-term business decisions.',
    category: 'consulting',
    icon: '🎯',
    teamConfig: {
      name: 'Business Strategy & Planning Team',
      description: 'Multi-agent team for strategic business planning and analysis',
      strategy: TeamCoordinationStrategy.CONSENSUS,
      configuration: {
        consensusThreshold: 0.75,
      },
      feedback_enabled: true,
      max_iterations: 4,
      members: [],
    },
    recommendedAgents: [
      {
        role: AgentRole.COORDINATOR,
        name: 'Strategy Consultant',
        description: 'Leads strategic planning',
        systemPrompt: 'You are a Strategy Consultant. Analyze market dynamics, develop strategic options, and provide recommendations. Use frameworks like SWOT, Porter\'s Five Forces, and BCG Matrix.',
        executionOrder: 1,
        isRequired: true,
      },
      {
        role: AgentRole.RESEARCHER,
        name: 'Market Researcher',
        description: 'Analyzes market trends',
        systemPrompt: 'You are a Market Researcher. Research market trends, customer needs, and competitive landscape. Provide data-driven market insights.',
        executionOrder: 2,
        isRequired: true,
      },
      {
        role: AgentRole.ANALYST,
        name: 'Financial Analyst',
        description: 'Analyzes financial implications',
        systemPrompt: 'You are a Financial Analyst. Analyze financial data, create models, and evaluate business opportunities. Provide financial projections and ROI analysis.',
        executionOrder: 3,
        isRequired: true,
      },
      {
        role: AgentRole.SPECIALIST,
        name: 'Innovation Strategist',
        description: 'Identifies innovation opportunities',
        systemPrompt: 'You are an Innovation Strategist. Identify emerging trends, evaluate new technologies, and recommend innovation initiatives. Think creatively about future opportunities.',
        executionOrder: 4,
        isRequired: false,
      },
      {
        role: AgentRole.REVIEWER,
        name: 'Strategy Reviewer',
        description: 'Reviews and validates strategy',
        systemPrompt: 'You are a Strategy Reviewer. Evaluate strategic recommendations for feasibility, risk, and alignment with business goals. Challenge assumptions and provide critical feedback.',
        executionOrder: 5,
        isRequired: true,
      },
    ],
  },

  // 9. General Purpose Team
  {
    id: 'general-purpose',
    name: 'General Purpose Team',
    description: 'Flexible team structure for custom workflows. Start here and adapt to your specific needs.',
    category: 'general',
    icon: '⚡',
    teamConfig: {
      name: 'General Purpose Team',
      description: 'Flexible multi-agent team for custom tasks',
      strategy: TeamCoordinationStrategy.SEQUENTIAL,
      configuration: {},
      feedback_enabled: false,
      max_iterations: 1,
      members: [],
    },
    recommendedAgents: [
      {
        role: AgentRole.COORDINATOR,
        name: 'Team Coordinator',
        description: 'Coordinates team activities',
        systemPrompt: 'You are a Team Coordinator. Organize tasks, delegate to team members, and synthesize results. Adapt to the specific needs of each task.',
        executionOrder: 1,
        isRequired: true,
      },
      {
        role: AgentRole.SPECIALIST,
        name: 'Task Specialist',
        description: 'Executes specialized tasks',
        systemPrompt: 'You are a Task Specialist. Execute assigned tasks with expertise and attention to detail. Adapt your approach based on task requirements.',
        executionOrder: 2,
        isRequired: true,
      },
      {
        role: AgentRole.REVIEWER,
        name: 'Quality Reviewer',
        description: 'Reviews work quality',
        systemPrompt: 'You are a Quality Reviewer. Review outputs for quality, completeness, and accuracy. Provide constructive feedback for improvements.',
        executionOrder: 3,
        isRequired: false,
      },
    ],
  },
]

/**
 * Get template by ID
 */
export function getTemplateById(id: string): TeamTemplate | undefined {
  return TEAM_TEMPLATES.find((template) => template.id === id)
}

/**
 * Get templates by category
 */
export function getTemplatesByCategory(category: TeamTemplate['category']): TeamTemplate[] {
  return TEAM_TEMPLATES.filter((template) => template.category === category)
}

/**
 * Get all template categories
 */
export function getTemplateCategories(): Array<{
  id: TeamTemplate['category']
  label: string
  count: number
}> {
  const categories: Array<{
    id: TeamTemplate['category']
    label: string
  }> = [
    { id: 'consulting', label: 'Consulting' },
    { id: 'technical', label: 'Technical' },
    { id: 'creative', label: 'Creative' },
    { id: 'analysis', label: 'Analysis' },
    { id: 'general', label: 'General' },
  ]

  return categories.map((cat) => ({
    ...cat,
    count: getTemplatesByCategory(cat.id).length,
  }))
}
